// pages/reports/ProfitLossReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
    ChevronUp, 
    RotateCcw, 
    Download,
    Printer,
   
    TrendingUp,
    TrendingDown,
    PieChart,
    FileText,
    Calendar,
    ShoppingBag,
    
    Folder,
    RefreshCw,
   
    Percent
} from 'feather-icons-react/build/IconComponents';
import { reportActions } from '../../core/action/reportActions';

const MySwal = withReactContent(Swal);

const ProfitLossReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    
    // State
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('summary');
    const [compareWith, setCompareWith] = useState('');
    
    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate),
                compare_with: compareWith
            };
            
            const result = await reportActions.getProfitLossReport(params);
            
            if (result.success) {
                setReportData(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, compareWith]);
    
    // Load on mount and when dependencies change
    useEffect(() => {
        loadReport();
    }, [loadReport]);
    
    // Handle export PDF
    const handleExportPDF = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToPDF('profitloss', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `profit_loss_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export PDF',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle export Excel
    const handleExportExcel = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToExcel('profitloss', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `profit_loss_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting Excel:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export Excel',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle print
    const handlePrint = () => {
        window.print();
    };
    
    // Compare options
    const compareOptions = [
        { value: '', label: 'No Comparison' },
        { value: 'previous', label: 'Previous Period' },
        { value: 'previous_year', label: 'Previous Year' }
    ];
    
    // Render filter bar
    const renderFilterBar = () => {
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={setFromDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={setToDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Compare with</label>
                            <Select
                                options={compareOptions}
                                value={compareOptions.find(opt => opt.value === compareWith)}
                                onChange={(opt) => setCompareWith(opt?.value || '')}
                                placeholder="Select comparison"
                                isClearable
                            />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={loadReport}>
                                <RefreshCw size={16} className="me-2" />
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render summary cards
    const renderSummaryCards = () => {
        if (!reportData) return null;
        
        const { summary } = reportData;
        const isProfitable = summary.net_profit > 0;
        
        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Income</p>
                                    <h3 className="mb-0 text-success">
                                        {reportActions.formatCurrency(summary.total_income)}
                                    </h3>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Expenses</p>
                                    <h3 className="mb-0 text-danger">
                                        {reportActions.formatCurrency(summary.total_expenses)}
                                    </h3>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <TrendingDown size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Gross Profit</p>
                                    <h3 className={`mb-0 ${summary.gross_profit >= 0 ? 'text-primary' : 'text-danger'}`}>
                                        {reportActions.formatCurrency(summary.gross_profit)}
                                    </h3>
                                    <small className="text-muted">Gross Margin: {summary.gross_profit_margin}%</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <ShoppingBag size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Net Profit</p>
                                    <h3 className={`mb-0 ${isProfitable ? 'text-success' : 'text-danger'}`}>
                                        {reportActions.formatCurrency(summary.net_profit)}
                                    </h3>
                                    <small className="text-muted">Margin: {summary.profit_margin}%</small>
                                </div>
                                <div className={`bg-${isProfitable ? 'success' : 'danger'} bg-opacity-10 rounded-circle p-3`}>
                                    <Percent size={24} className={isProfitable ? 'text-success' : 'text-danger'} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render tabs
    const renderTabs = () => {
        const tabs = [
            { id: 'summary', label: 'P&L Statement', icon: <PieChart size={16} /> },
            { id: 'income', label: 'Income Details', icon: <TrendingUp size={16} /> },
            { id: 'expenses', label: 'Expense Details', icon: <Folder size={16} /> },
            { id: 'monthly', label: 'Monthly Breakdown', icon: <Calendar size={16} /> }
        ];
        
        return (
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span className="ms-2">{tab.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    };
    
    // Render summary tab (P&L Statement)
    const renderSummaryTab = () => {
        if (!reportData) return null;
        
        const { summary, income_breakdown, expense_breakdown } = reportData;
        
        return (
            <div className="card">
                <div className="card-header bg-primary bg-opacity-10">
                    <h5 className="mb-0">Profit & Loss Statement</h5>
                    <small className="text-muted">
                        For the period {reportActions.formatDate(reportData.period.from_date)} to {reportActions.formatDate(reportData.period.to_date)}
                    </small>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-bordered">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '60%' }}>Particulars</th>
                                    <th className="text-end" style={{ width: '20%' }}>Amount</th>
                                    <th className="text-end" style={{ width: '20%' }}>% of Income</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Income Section */}
                                <tr className="table-success">
                                    <td colSpan="3"><strong>INCOME</strong></td>
                                </tr>
                                
                                <tr>
                                    <td className="ps-4">Sales Income</td>
                                    <td className="text-end">{reportActions.formatCurrency(income_breakdown.sales.amount)}</td>
                                    <td className="text-end">
                                        {((income_breakdown.sales.amount / summary.total_income) * 100).toFixed(2)}%
                                    </td>
                                </tr>
                                
                                {income_breakdown.other.map((item, idx) => (
                                    <tr key={idx}>
                                        <td className="ps-4">{item.category}</td>
                                        <td className="text-end">{reportActions.formatCurrency(item.amount)}</td>
                                        <td className="text-end">
                                            {((item.amount / summary.total_income) * 100).toFixed(2)}%
                                        </td>
                                    </tr>
                                ))}
                                
                                <tr className="table-success">
                                    <td><strong>Total Income</strong></td>
                                    <td className="text-end"><strong>{reportActions.formatCurrency(summary.total_income)}</strong></td>
                                    <td className="text-end"><strong>100%</strong></td>
                                </tr>
                                
                                {/* Expense Section */}
                                <tr className="table-danger">
                                    <td colSpan="3"><strong>EXPENSES</strong></td>
                                </tr>
                                
                                <tr>
                                    <td className="ps-4">Cost of Goods Sold</td>
                                    <td className="text-end text-danger">{reportActions.formatCurrency(expense_breakdown.cogs.amount)}</td>
                                    <td className="text-end">
                                        {((expense_breakdown.cogs.amount / summary.total_income) * 100).toFixed(2)}%
                                    </td>
                                </tr>
                                
                                <tr>
                                    <td className="ps-4">Purchase Expenses</td>
                                    <td className="text-end text-danger">{reportActions.formatCurrency(expense_breakdown.purchases.amount)}</td>
                                    <td className="text-end">
                                        {((expense_breakdown.purchases.amount / summary.total_income) * 100).toFixed(2)}%
                                    </td>
                                </tr>
                                
                                {expense_breakdown.operating.map((item, idx) => (
                                    <tr key={idx}>
                                        <td className="ps-4">{item.category}</td>
                                        <td className="text-end text-danger">{reportActions.formatCurrency(item.amount)}</td>
                                        <td className="text-end">
                                            {((item.amount / summary.total_income) * 100).toFixed(2)}%
                                        </td>
                                    </tr>
                                ))}
                                
                                <tr className="table-danger">
                                    <td><strong>Total Expenses</strong></td>
                                    <td className="text-end"><strong>{reportActions.formatCurrency(summary.total_expenses)}</strong></td>
                                    <td className="text-end">
                                        <strong>{((summary.total_expenses / summary.total_income) * 100).toFixed(2)}%</strong>
                                    </td>
                                </tr>
                                
                                {/* Gross Profit */}
                                <tr className="table-primary">
                                    <td><strong>Gross Profit</strong></td>
                                    <td className="text-end">
                                        <strong>{reportActions.formatCurrency(summary.gross_profit)}</strong>
                                    </td>
                                    <td className="text-end">
                                        <strong>{summary.gross_profit_margin}%</strong>
                                    </td>
                                </tr>
                                
                                {/* Net Profit */}
                                <tr className={summary.net_profit >= 0 ? 'table-success' : 'table-danger'}>
                                    <td><strong>Net Profit</strong></td>
                                    <td className="text-end">
                                        <strong>{reportActions.formatCurrency(summary.net_profit)}</strong>
                                    </td>
                                    <td className="text-end">
                                        <strong>{summary.profit_margin}%</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render income details tab
    const renderIncomeTab = () => {
        if (!reportData) return null;
        
        const { income_breakdown } = reportData;
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Income Details</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Source</th>
                                    <th className="text-end">Transactions</th>
                                    <th className="text-end">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="table-success">
                                    <td colSpan="3"><strong>Sales Income</strong></td>
                                </tr>
                                <tr>
                                    <td className="ps-4">Sales</td>
                                    <td className="text-end">{income_breakdown.sales.transaction_count}</td>
                                    <td className="text-end text-success">{reportActions.formatCurrency(income_breakdown.sales.amount)}</td>
                                </tr>
                                
                                {income_breakdown.other.length > 0 && (
                                    <>
                                        <tr className="table-info">
                                            <td colSpan="3"><strong>Other Income</strong></td>
                                        </tr>
                                        {income_breakdown.other.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="ps-4">{item.category}</td>
                                                <td className="text-end">{item.transaction_count}</td>
                                                <td className="text-end text-info">{reportActions.formatCurrency(item.amount)}</td>
                                            </tr>
                                        ))}
                                    </>
                                )}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th>Total Income</th>
                                    <th className="text-end">
                                        {income_breakdown.sales.transaction_count + 
                                         income_breakdown.other.reduce((sum, i) => sum + i.transaction_count, 0)}
                                    </th>
                                    <th className="text-end text-success">
                                        {reportActions.formatCurrency(reportData.summary.total_income)}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render expense details tab
    const renderExpensesTab = () => {
        if (!reportData) return null;
        
        const { expense_breakdown } = reportData;
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Expense Details</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Expense Category</th>
                                    <th className="text-end">Details</th>
                                    <th className="text-end">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="table-danger">
                                    <td colSpan="3"><strong>Cost of Goods Sold</strong></td>
                                </tr>
                                <tr>
                                    <td className="ps-4">COGS</td>
                                    <td className="text-end">{expense_breakdown.cogs.product_count} products, {expense_breakdown.cogs.total_quantity} units</td>
                                    <td className="text-end text-danger">{reportActions.formatCurrency(expense_breakdown.cogs.amount)}</td>
                                </tr>
                                
                                <tr className="table-warning">
                                    <td colSpan="3"><strong>Purchase Expenses</strong></td>
                                </tr>
                                <tr>
                                    <td className="ps-4">Purchases</td>
                                    <td className="text-end">{expense_breakdown.purchases.transaction_count} transactions</td>
                                    <td className="text-end text-warning">{reportActions.formatCurrency(expense_breakdown.purchases.amount)}</td>
                                </tr>
                                
                                {expense_breakdown.operating.length > 0 && (
                                    <>
                                        <tr className="table-secondary">
                                            <td colSpan="3"><strong>Operating Expenses</strong></td>
                                        </tr>
                                        {expense_breakdown.operating.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="ps-4">{item.category}</td>
                                                <td className="text-end">{item.transaction_count} transactions</td>
                                                <td className="text-end text-secondary">{reportActions.formatCurrency(item.amount)}</td>
                                            </tr>
                                        ))}
                                    </>
                                )}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th colSpan="2">Total Expenses</th>
                                    <th className="text-end text-danger">
                                        {reportActions.formatCurrency(reportData.summary.total_expenses)}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render monthly tab
    const renderMonthlyTab = () => {
        if (!reportData?.monthly_breakdown || reportData.monthly_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No monthly data available.
                </div>
            );
        }
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Monthly Profit & Loss</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Month</th>
                                    <th className="text-end">Income</th>
                                    <th className="text-end">COGS</th>
                                    <th className="text-end">Expenses</th>
                                    <th className="text-end">Gross Profit</th>
                                    <th className="text-end">Net Profit</th>
                                    <th className="text-end">Margin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.monthly_breakdown.map((month, idx) => {
                                    const grossProfit = month.income - month.cogs;
                                    const netProfit = month.income - month.cogs - month.expenses;
                                    const margin = month.income > 0 ? (netProfit / month.income) * 100 : 0;
                                    
                                    return (
                                        <tr key={idx}>
                                            <td><strong>{month.month}</strong></td>
                                            <td className="text-end text-success">{reportActions.formatCurrency(month.income)}</td>
                                            <td className="text-end text-danger">{reportActions.formatCurrency(month.cogs)}</td>
                                            <td className="text-end text-warning">{reportActions.formatCurrency(month.expenses)}</td>
                                            <td className={`text-end ${grossProfit >= 0 ? 'text-primary' : 'text-danger'}`}>
                                                {reportActions.formatCurrency(grossProfit)}
                                            </td>
                                            <td className={`text-end ${netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                {reportActions.formatCurrency(netProfit)}
                                            </td>
                                            <td className="text-end">{margin.toFixed(2)}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);
    
    if (loading && !reportData) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Profit & Loss Report...</h5>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Profit & Loss Report</h4>
                            <h6>Analyze business profitability</h6>
                        </div>
                    </div>
                    
                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-danger" onClick={handleExportPDF}>
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success" onClick={handleExportExcel}>
                                    <FileText size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={loadReport}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>
                
                {/* Period Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Period:</strong> {reportActions.formatDate(reportData?.period.from_date)} to {reportActions.formatDate(reportData?.period.to_date)}
                                    </div>
                                    <div>
                                        <strong>Net Profit:</strong> 
                                        <span className={reportData?.summary.net_profit >= 0 ? 'text-success ms-2' : 'text-danger ms-2'}>
                                            {reportActions.formatCurrency(reportData?.summary.net_profit || 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Filter Bar */}
                {renderFilterBar()}
                
                {/* Summary Cards */}
                {renderSummaryCards()}
                
                {/* Tabs */}
                {renderTabs()}
                
                {/* Tab Content */}
                {activeTab === 'summary' && renderSummaryTab()}
                {activeTab === 'income' && renderIncomeTab()}
                {activeTab === 'expenses' && renderExpensesTab()}
                {activeTab === 'monthly' && renderMonthlyTab()}
            </div>
        </div>
    );
};

export default ProfitLossReport;